import api from "./axios";
import type { Activity, AchievementCredit, ActivityRoster } from "../types";

export const activitiesApi = {
  list: async (includeArchived = false): Promise<Activity[]> => {
    const res = await api.get<Activity[]>("/activities", {
      params: includeArchived ? { includeArchived: "true" } : undefined,
    });
    return res.data;
  },

  get: async (id: number): Promise<Activity> => {
    const res = await api.get<Activity>(`/activities/${id}`);
    return res.data;
  },

  create: async (data: Omit<Activity, "id" | "achievementCredits">): Promise<Activity> => {
    const res = await api.post<Activity>("/activities", data);
    return res.data;
  },

  update: async (
    id: number,
    data: Partial<Omit<Activity, "id" | "achievementCredits">>
  ): Promise<Activity> => {
    const res = await api.put<Activity>(`/activities/${id}`, data);
    return res.data;
  },

  archive: async (id: number): Promise<Activity> => {
    const res = await api.patch<Activity>(`/activities/${id}/archive`);
    return res.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/activities/${id}`);
  },

  getCredits: async (activityId: number): Promise<AchievementCredit[]> => {
    const res = await api.get<AchievementCredit[]>(`/activities/${activityId}/credits`);
    return res.data;
  },

  addCredit: async (
    activityId: number,
    data: Omit<AchievementCredit, "id" | "activityId" | "type" | "sortOrder" | "displayNumber">
  ): Promise<AchievementCredit> => {
    const res = await api.post<AchievementCredit>(
      `/activities/${activityId}/credits`,
      data
    );
    return res.data;
  },

  updateCredit: async (
    activityId: number,
    creditId: number,
    data: Pick<AchievementCredit, "name" | "code" | "description">
  ): Promise<AchievementCredit> => {
    const res = await api.put<AchievementCredit>(
      `/activities/${activityId}/credits/${creditId}`,
      data
    );
    return res.data;
  },

  archiveCredit: async (activityId: number, creditId: number): Promise<AchievementCredit> => {
    const res = await api.patch<AchievementCredit>(
      `/activities/${activityId}/credits/${creditId}/archive`
    );
    return res.data;
  },

  deleteCredit: async (activityId: number, creditId: number): Promise<void> => {
    await api.delete(`/activities/${activityId}/credits/${creditId}`);
  },

  reorderCredits: async (activityId: number, orderedIds: number[]): Promise<void> => {
    await api.put(`/activities/${activityId}/credits/reorder`, { orderedIds });
  },

  /** With writeInIds, orderedIds[i] is the activity in kerchief slot i (null = empty). */
  reorder: async (orderedIds: (number | null)[], writeInIds?: number[]): Promise<void> => {
    await api.put(`/activities/reorder`, writeInIds ? { orderedIds, writeInIds } : { orderedIds });
  },

  getRoster: async (activityId: number, year: number): Promise<ActivityRoster> => {
    const res = await api.get<ActivityRoster>(`/activities/${activityId}/roster`, { params: { year } });
    return res.data;
  },

  uploadBadge: async (id: number, file: File): Promise<Activity> => {
    const form = new FormData();
    form.append("badge", file);
    const res = await api.post<Activity>(`/activities/${id}/badge`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  removeBadge: async (id: number): Promise<Activity> => {
    const res = await api.delete<Activity>(`/activities/${id}/badge`);
    return res.data;
  },
};
